import type { Doc } from "building-types/doc";
import type { RoomEdge } from "building-types/handles/docEntityHandleTypes";
import type { EdgeModifier } from "building-types/modifiers/edgeModifierTypes";
import type { Modifier } from "building-types/modifiers/modifierTypes";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import { aabb2ByTwoPoints, aabb2Grow, aabb2Overlaps } from "geom/aabb2";
import { DIST_EPSILON, WindingDir } from "geom/core";
import type { DCEL } from "geom/polyclip/dcel";
import { dcelFromPolygons } from "geom/polyclip/dcelFromPolygons";
import { polyLine2ToPolyClipPolygonLoop } from "geom/polyclip/polyClip";
import { mutateRoomOutlineUsingEdgeModifiers } from "../utils/modifierUtils";

const DIST_SMALL = 0.001;

type PartialRoom = Pick<StaticRoom, "outline" | "edgeIds" | "id">;

function getRoomsDCEL(rooms: PartialRoom[]): DCEL | null {
  const polygons = rooms.map((r) => {
    if (!r.outline) {
      console.assert(false, "Invalid room outline: outline is required.");
      return [];
    } else if (!(r.outline instanceof Array)) {
      if (!(r.outline instanceof Float64Array)) {
        console.assert(
          false,
          `Invalid room outline type: outline should be a typed array but instead it is a ${typeof r.outline}`,
        );
        return [];
      }
    }
    return [polyLine2ToPolyClipPolygonLoop(r.outline, false, true, WindingDir.CW)];
  });
  return dcelFromPolygons({ polygons });
}

export function applyEdgeModifierToARoom(
  room: StaticRoom,
  modifier: Modifier,
  filteredEdgeModifiers: EdgeModifier[],
): PartialRoom | null {
  if (!room.outline) {
    console.assert(false, "Invalid room outline: outline is required.");
    return null;
  } else if (!(room.outline instanceof Array)) {
    if (!(room.outline instanceof Float64Array)) {
      console.assert(
        false,
        `Invalid room outline type: outline should be a typed array but instead it is a ${typeof room.outline}`,
      );
      return null;
    }
  }

  const mutatedOutline = mutateRoomOutlineUsingEdgeModifiers(
    room.outline,
    filteredEdgeModifiers,
    room.edgeIds,
    modifier.moveX ?? 0,
    modifier.moveY ?? 0,
  );
  return { outline: mutatedOutline, edgeIds: room.edgeIds, id: room.id };
}

function getInteriorHalfEdgePairs(dcel: DCEL): [number, number][] {
  const interiorEdgePairs: [number, number][] = [];
  const edgeMap = new Map<string, number>();

  for (let i = 0; i < dcel.halfEdges.length; i++) {
    const [start, end] = dcel.halfEdges[i];
    const edgeKey = `${start},${end}`;
    const oppositeKey = `${end},${start}`;

    if (edgeMap.has(oppositeKey)) {
      const oppositeIndex = edgeMap.get(oppositeKey);
      if (oppositeIndex === undefined) {
        continue;
      }
      const faceA = dcel.halfEdgeToFace[i];
      const faceB = dcel.halfEdgeToFace[oppositeIndex];

      if (dcel.faceIsInterior(faceA) && dcel.faceIsInterior(faceB)) {
        interiorEdgePairs.push([i, oppositeIndex]);
      }
    } else {
      edgeMap.set(edgeKey, i);
    }
  }

  return interiorEdgePairs;
}

const SHRINKAGE_PERCENTAGE: number = 0.001;

function findOverlappingRoomEdge(x1: number, y1: number, x2: number, y2: number, room: PartialRoom): RoomEdge | null {
  //only compute once the length of the input edge and unit vector and aaBBInputEdge
  const lengthInputEdge = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

  if (lengthInputEdge < DIST_EPSILON) {
    return null; //ignore zero length edges
  }

  const [u1x, u1y] = [(x2 - x1) / lengthInputEdge, (y2 - y1) / lengthInputEdge];
  const [shrinkX1, shrinkY1] = [x1 + u1x * SHRINKAGE_PERCENTAGE, y1 + u1y * SHRINKAGE_PERCENTAGE];
  const [shrinkX2, shrinkY2] = [x2 - u1x * SHRINKAGE_PERCENTAGE, y2 - u1y * SHRINKAGE_PERCENTAGE];

  const aabbInputEdge = aabb2Grow(aabb2ByTwoPoints(shrinkX1, shrinkY1, shrinkX2, shrinkY2), DIST_EPSILON, DIST_EPSILON);

  const { outline, edgeIds, id: roomId } = room;

  if (!outline || !edgeIds) {
    return null;
  }

  for (let i = 0; i < outline.length - 2; i += 2) {
    const x3 = outline[i];
    const y3 = outline[i + 1];
    const x4 = outline[i + 2];
    const y4 = outline[i + 3];

    const lengthOutlineEdge = Math.sqrt((x4 - x3) ** 2 + (y4 - y3) ** 2);

    if (lengthOutlineEdge < DIST_EPSILON) {
      continue; //ignore zero length edges
    }

    const [u2x, u2y] = [(x4 - x3) / lengthOutlineEdge, (y4 - y3) / lengthOutlineEdge];

    //shrink the room edge by 2*DIST_EPSILON
    const [shrinkX3, shrinkY3] = [x3 + u2x * SHRINKAGE_PERCENTAGE, y3 + u2y * SHRINKAGE_PERCENTAGE];
    const [shrinkX4, shrinkY4] = [x4 - u2x * SHRINKAGE_PERCENTAGE, y4 - u2y * SHRINKAGE_PERCENTAGE];

    const aabbRoomEdge = aabb2Grow(
      aabb2ByTwoPoints(shrinkX3, shrinkY3, shrinkX4, shrinkY4),
      DIST_EPSILON,
      DIST_EPSILON,
    );

    //do simple AABB overlap check before doing more expensive checks
    if (aabb2Overlaps(aabbInputEdge, aabbRoomEdge)) {
      // check if the input edge is in the same direction as the room edge
      const lengthRoomEdge = Math.sqrt((x4 - x3) ** 2 + (y4 - y3) ** 2);
      const [unitRoomDirX, unitRoomDirY] = [(x4 - x3) / lengthRoomEdge, (y4 - y3) / lengthRoomEdge];
      const absCrossProduct = Math.abs(u1x * unitRoomDirY - u1y * unitRoomDirX);

      if (absCrossProduct < DIST_EPSILON) {
        const dist1 = Math.sqrt((x1 - x3) ** 2 + (y1 - y3) ** 2);
        const dist2 = Math.sqrt((x2 - x4) ** 2 + (y2 - y4) ** 2);

        //overlap check
        if (dist1 < DIST_SMALL && dist2 < DIST_SMALL) {
          return { roomId, edgeId: edgeIds[i / 2] };
        }

        const dist3 = Math.sqrt((x1 - x4) ** 2 + (y1 - y4) ** 2);
        const dist4 = Math.sqrt((x2 - x3) ** 2 + (y2 - y3) ** 2);

        //overlap check but with opposite direction
        if (dist3 < DIST_SMALL && dist4 < DIST_SMALL) {
          return { roomId, edgeId: edgeIds[i / 2] };
        }

        //partial overlap check
        if (Math.abs(dist1 + dist3 - lengthRoomEdge) < DIST_SMALL) {
          return { roomId, edgeId: edgeIds[i / 2] };
        }

        //partial overlap check
        if (Math.abs(dist2 + dist4 - lengthRoomEdge) < DIST_SMALL) {
          return { roomId, edgeId: edgeIds[i / 2] };
        }

        //partial overlap check
        if (Math.abs(dist1 + dist4 - lengthInputEdge) < DIST_SMALL) {
          return { roomId, edgeId: edgeIds[i / 2] };
        }

        //partial overlap check
        if (Math.abs(dist2 + dist3 - lengthInputEdge) < DIST_SMALL) {
          return { roomId, edgeId: edgeIds[i / 2] };
        }
      }
    }
  }
  return null;
}

function calculateOverlapEdgeMatchesMap(
  dcel: DCEL,
  levelRooms: PartialRoom[],
  overlapEdgeMatchesMap: { [hash: string]: [RoomEdge, RoomEdge] },
) {
  const interiorEdgePairs = getInteriorHalfEdgePairs(dcel);

  const { vertices } = dcel;

  for (const [halfEdgeIndex1, halfEdgeIndex2] of interiorEdgePairs) {
    const polygons1 = dcel.halfEdgeToPolygon[halfEdgeIndex1];
    const polygons2 = dcel.halfEdgeToPolygon[halfEdgeIndex2];

    for (const polygon1 of polygons1) {
      if (polygon1 >= levelRooms.length) {
        continue;
      }
      for (const polygon2 of polygons2) {
        if (polygon2 >= levelRooms.length || polygon1 === polygon2) {
          continue;
        }
        const startVertex = vertices[dcel.halfEdges[halfEdgeIndex1][0]];
        const endVertex = vertices[dcel.halfEdges[halfEdgeIndex1][1]];

        const room1AndEdge = findOverlappingRoomEdge(
          startVertex[0],
          startVertex[1],
          endVertex[0],
          endVertex[1],
          levelRooms[polygon1],
        );
        if (!room1AndEdge) {
          continue;
        }
        const room2AndEdge = findOverlappingRoomEdge(
          startVertex[0],
          startVertex[1],
          endVertex[0],
          endVertex[1],
          levelRooms[polygon2],
        );
        if (!room2AndEdge) {
          continue;
        }

        const hash = symmetricInteriorRoomEdgeDimensionHash([room1AndEdge, room2AndEdge]);
        if (!overlapEdgeMatchesMap[hash]) {
          overlapEdgeMatchesMap[hash] = [room1AndEdge, room2AndEdge];
        }
      }
    }
  }
}

export function getOverlappingEdgeMatches(
  docFields: Pick<Doc, "levels" | "rooms" | "blocks" | "edgeModifiers" | "modifiers">,
) {
  const { levels, rooms, blocks, edgeModifiers, modifiers } = docFields;
  const overlapEdgeMatchesMap: { [hash: string]: [RoomEdge, RoomEdge] } = {};

  //update the map with the edge modifiers applied
  for (const levelId in levels) {
    const blockIds = Object.values(blocks)
      .filter((b) => b.levelId === levelId)
      .map((b) => b.id);
    const levelRooms = Object.values(rooms).filter((r) => r?.parentId && blockIds.includes(r.parentId));

    //collect all the rooms for this level
    const mutatedRooms: PartialRoom[] = levelRooms.map((r) => {
      return { outline: r.outline, edgeIds: r.edgeIds, id: r.id };
    });

    //collect all the edge modifiers for this level
    const edgeModifiersForLevel = Object.values(edgeModifiers).filter((em) => {
      const parentRoom = rooms[em.parentId];

      // Check if parentRoom exists and if its blockId is included in blockIds
      return parentRoom && parentRoom.parentId && blockIds.includes(parentRoom.parentId);
    });

    //apply the edge modifier one at a time and that room to the mutatedRooms array
    //this will generate multiple overlapping polygons but that is ok as our dcel can handle it
    for (const em of edgeModifiersForLevel) {
      const modifier = modifiers[em.modifierId];
      if (!modifier) {
        continue;
      }
      const mutatedRoom = applyEdgeModifierToARoom(rooms[em.parentId], modifier, [em]);
      if (!mutatedRoom) {
        continue;
      }
      mutatedRooms.push(mutatedRoom);
    }

    const dcel = getRoomsDCEL(mutatedRooms);

    if (!dcel) {
      continue;
    }
    calculateOverlapEdgeMatchesMap(dcel, mutatedRooms, overlapEdgeMatchesMap);
  }
  return Object.values(overlapEdgeMatchesMap);
}

export function symmetricInteriorRoomEdgeDimensionHash(roomEdges: [RoomEdge, RoomEdge]): string {
  const roomEdgeSide1 = `${roomEdges[0].roomId}.${roomEdges[0].edgeId}`;
  const roomEdgeSide2 = `${roomEdges[1].roomId}.${roomEdges[1].edgeId}`;
  return [roomEdgeSide1, roomEdgeSide2].sort().join(".");
}

export function getRoomEdgeArray(doc: Pick<Doc, "rooms">): RoomEdge[] {
  //loop over all the rooms and find the opposite edge matches i.e. create a combination of all the
  //edges in a room and add return an array of all the opposite edge matches
  const { rooms } = doc;
  const allRoomEdges: RoomEdge[] = [];

  for (const room of Object.values(rooms)) {
    const roomEdges = room?.edgeIds?.map((edgeId) => ({ roomId: room.id, edgeId }) as RoomEdge) ?? [];

    allRoomEdges.push(...roomEdges);
  }
  return allRoomEdges;
}
